import { TCompactType, IGridItem, IPoint, TLayout } from './grid.types'

type TMoveElementParams = {
  layout: TLayout
  el: IGridItem
  cols: number
  compactType: TCompactType
  x?: number
  y?: number
  isUserAction?: boolean
  preventCollision?: boolean
}

const moveElement = (params: TMoveElementParams): TLayout => {
  const { el, cols, compactType, x, y, isUserAction, preventCollision } = params
  let layout = params.layout
  if (el.y === y && el.x === x) return layout

  // console.log(`Moving element ${el.key} to [${String(x)},${String(y)}] from [${el.x},${el.y}]`)
  const oldX = el.x
  const oldY = el.y

  if (typeof x === 'number') el.x = x
  if (typeof y === 'number') el.y = y
  el.moved = true
  let sorted = sortLayoutItems(layout, compactType)
  let movingUp = false
  if (compactType === 'vertical' && typeof y === 'number') {
    movingUp = oldY >= y
  } else if (compactType === 'horizontal' && typeof x === 'number') {
    movingUp = oldX >= x
  }
  if (movingUp) sorted = sorted.reverse()
  const collisions = getAllCollisions(sorted, el)
  if (collisions.length > 0 && preventCollision) {
    // console.log(`Collision prevented on ${el.key}, reverting.`)
    el.x = oldX
    el.y = oldY
    el.moved = false
    return layout // did not change so don't clone
  }

  // Move each item that collides away from this element.
  for (let i = 0, len = collisions.length; i < len; i++) {
    const collision = collisions[i]
    // console.log(
    //   `Resolving collision between ${el.key} at [${el.x},${el.y}] and ${collision.key} at [${collision.x},${collision.y}]`
    // )
    // Short circuit so we can't infinite loop

    if (collision.moved) continue

    // Don't move static items - we have to move *this* element away
    if (collision.static) {
      layout = moveElementAwayFromCollision(layout, collision, el, compactType, cols, isUserAction)
    } else {
      layout = moveElementAwayFromCollision(layout, el, collision, compactType, cols, isUserAction)
    }
  }

  return layout
}
const sortLayoutItems = (layout: TLayout, compactType: TCompactType): TLayout => {
  if (compactType === 'horizontal') return sortLayoutItemsByColRow(layout)
  if (compactType === 'vertical') return sortLayoutItemsByRowCol(layout)
  else return layout
}

const sortLayoutItemsByRowCol = (layout: TLayout): TLayout => {
  // Slice to clone array as sort modifies
  return layout.slice(0).sort((a, b) => {
    if (a.y > b.y || (a.y === b.y && a.x > b.x)) {
      return 1
    } else if (a.y === b.y && a.x === b.x) {
      // Without this, we can get different sort results in IE vs. Chrome/FF
      return 0
    }
    return -1
  })
}

/**
 * Sort layout items by column ascending then row ascending.
 *
 * Does not modify Layout.
 */
const sortLayoutItemsByColRow = (layout: TLayout): TLayout => {
  return layout.slice(0).sort(function (a, b) {
    if (a.x > b.x || (a.x === b.x && a.y > b.y)) {
      return 1
    }
    return -1
  })
}

const moveElementAwayFromCollision = (
  layout: TLayout,
  collidesWith: IGridItem,
  itemToMove: IGridItem,
  compactType: TCompactType,
  cols: number,
  isUserAction?: boolean
): TLayout => {
  const compactH = compactType === 'horizontal'
  // Compact vertically if not set to horizontal
  const compactV = compactType === 'vertical'
  // we're already colliding (not for static items)

  // If there is enough space above the collision to put this element, move it there.
  // We only do this on the main collision as this can get funky in cascades and cause
  // unwanted swapping behavior.
  if (isUserAction) {
    // Reset isUserAction flag because we're not in the main collision anymore.
    isUserAction = false

    // Make a mock item so we don't modify the item here, only modify in moveElement.
    const fakeItem: IGridItem = {
      x: compactH ? Math.max(collidesWith.x - itemToMove.width, 0) : itemToMove.x,
      y: compactV ? Math.max(collidesWith.y - itemToMove.height, 0) : itemToMove.y,
      width: itemToMove.width,
      height: itemToMove.height,
      children: null,
      key: 'fakeItem',
      static: false,
    }

    const firstCollision = getFirstCollision(layout, fakeItem)
    const collisionNorth = firstCollision && firstCollision.y + firstCollision.height > collidesWith.y
    const collisionWest = firstCollision && collidesWith.x + collidesWith.width > firstCollision.x

    // No collision? If so, we can go up there; otherwise, we'll end up moving down as normal
    if (!firstCollision) {
      // console.log(`Doing reverse collision on ${itemToMove.key} up to [${fakeItem.x},${fakeItem.y}].`)
      return moveElement({
        layout,
        el: itemToMove,
        cols,
        compactType,
        x: compactH ? fakeItem.x : undefined,
        y: compactV ? fakeItem.y : undefined,
        isUserAction,
      })
    } else if (collisionNorth && compactV) {
      return moveElement({ layout, el: itemToMove, cols, compactType, y: collidesWith.y + 1, isUserAction })
    } else if (collisionNorth && compactType == null) {
      collidesWith.y = itemToMove.y
      itemToMove.y = itemToMove.y + itemToMove.height

      return layout
    } else if (collisionWest && compactH) {
      return moveElement({ layout, el: collidesWith, cols, compactType, x: itemToMove.x, isUserAction })
    }
  }

  const newX = compactH ? itemToMove.x + 1 : undefined
  const newY = compactV ? itemToMove.y + 1 : undefined

  if (newX == null && newY == null) {
    return layout
  }
  return moveElement({
    layout,
    el: itemToMove,
    cols,
    compactType,
    x: compactH ? itemToMove.x + 1 : undefined,
    y: compactV ? itemToMove.y + 1 : undefined,
    isUserAction,
  })
}

const getFirstCollision = (layout: TLayout, layoutItem: IGridItem): IGridItem | null => {
  for (let i = 0, len = layout.length; i < len; i++) {
    if (collides(layout[i], layoutItem)) return layout[i]
  }
}

const getAllCollisions = (layout: TLayout, layoutItem: IGridItem): Array<IGridItem> => {
  return layout.filter((l) => collides(l, layoutItem))
}

const collides = (l1: IGridItem, l2: IGridItem): boolean => {
  if (l1.key === l2.key) return false // same element
  if (l1.x + l1.width <= l2.x) return false // l1 is left of l2
  if (l1.x >= l2.x + l2.width) return false // l1 is right of l2
  if (l1.y + l1.height <= l2.y) return false // l1 is above l2
  if (l1.y >= l2.y + l2.height) return false // l1 is below l2
  return true // boxes overlap
}

const compact = (layout: TLayout, compactType: TCompactType, cols: number): TLayout => {
  // Statics go in the compareWith array right away so items flow around them.

  const compareWith = layout.filter((el) => el.static)
  // We keep track of the bottom position.
  let b = bottom(compareWith)
  // We go through the items by row and column.
  const sorted = sortLayoutItems(layout, compactType)
  // Holding for new items.
  const out = Array(layout.length)

  for (let i = 0, len = sorted.length; i < len; i++) {
    let el = { ...sorted[i] }

    // Don't move static elements
    if (!el.static) {
      el = compactItem(compareWith, el, compactType, cols, sorted, b)
      b = Math.max(b, el.y + el.height)

      // Add to comparison array. We only collide with items before this one.
      // Statics are already in this array.
      compareWith.push(el)
    }

    // Add to output array to make sure they still come out in the right order.
    out[layout.indexOf(sorted[i])] = el

    // Clear moved flag, if it exists.
    el.moved = false
  }

  return out
}

const compactItem = (
  compareWith: TLayout,
  el: IGridItem,
  compactType: TCompactType,
  cols: number,
  fullLayout: TLayout,
  b: number
): IGridItem => {
  const compactV = compactType === 'vertical'
  const compactH = compactType === 'horizontal'
  if (compactV) {
    // Bottom 'y' possible is the bottom of the layout.
    // This allows you to do nice stuff like specify {y: Infinity}
    // This is here because the layout must be sorted in order to get the correct bottom `y`.
    el.y = Math.min(b, el.y)
    // Move the element up as far as it can go without colliding.

    while (el.y > 0 && !getFirstCollision(compareWith, el)) {
      el.y--
    }
  } else if (compactH) {
    // Move the element left as far as it can go without colliding.
    while (el.x > 0 && !getFirstCollision(compareWith, el)) {
      el.x--
    }
  }

  // Move it down, and keep moving it down if it's colliding.
  let collides = getFirstCollision(compareWith, el)
  let count = 0
  // Checking the compactType null value to avoid breaking the layout when overlapping is allowed.
  while (collides && !(compactType === null)) {
    if (count >= 100) return
    else count += 1
    if (compactH) {
      resolveCompactionCollision(fullLayout, el, collides.x + collides.width, 'x')
    } else {
      resolveCompactionCollision(fullLayout, el, collides.y + collides.height, 'y')
    }
    // Since we can't grow without bounds horizontally, if we've overflown, let's move it down and try again.
    if (compactH && el.x + el.width > cols) {
      el.x = cols - el.width
      el.y++
      // ALso move element as left as we can
      while (el.x > 0 && !getFirstCollision(compareWith, el)) {
        el.x--
      }
    }

    collides = getFirstCollision(compareWith, el)
  }

  // Ensure that there are no negative positions
  el.y = Math.max(el.y, 0)
  el.x = Math.max(el.x, 0)

  return el
}

const resolveCompactionCollision = (layout: TLayout, item: IGridItem, moveToCoord: number, axis: 'x' | 'y') => {
  const sizeProp = axis === 'x' ? 'width' : 'height'
  item[axis] += 1
  const itemIndex = layout
    .map((layoutItem) => {
      return layoutItem.key
    })
    .indexOf(item.key)

  // Go through each item we collide with.
  for (let i = itemIndex + 1; i < layout.length; i++) {
    const otherItem = layout[i]
    // Ignore static items
    if (otherItem.static) continue

    // Optimization: we can break early if we know we're past this el
    // We can do this b/c it's a sorted layout
    if (otherItem.y > item.y + item.height) break

    if (collides(item, otherItem)) {
      resolveCompactionCollision(layout, otherItem, moveToCoord + item[sizeProp], axis)
    }
  }

  item[axis] = moveToCoord
}

const bottom = (layout: TLayout): number => {
  let max = 0,
    bottomY
  for (let i = 0, len = layout.length; i < len; i++) {
    bottomY = layout[i].y + layout[i].height
    if (bottomY > max) max = bottomY
  }
  return max
}

/**
 * colCompact
 *
 * Перемещает элементы сетки, которые выходят за правую границу (x + width > cols), вниз под текущий layout.
 * Для каждого такого элемента:
 *   - Если ширина элемента больше количества колонок, она ограничивается значением cols.
 *   - Элемент перемещается в первую свободную строку под всеми остальными элементами (bottomBorder).
 * После обработки всех "выходящих" элементов выполняется компактизация layout (уплотнение без коллизий).
 *
 * @param layout - текущий массив элементов сетки (TLayout)
 * @param compactType - тип компактизации ('vertical' | 'horizontal' | 'null')
 * @param cols - количество колонок в сетке
 * @returns новый layout с корректно размещёнными элементами
 */
const colCompact = (layout: TLayout, compactType: TCompactType, cols: number) => {
  const overflowRightEls = layout.filter((item) => item.x + item.width > cols)

  let bottomBorder = bottom(layout)
  for (let el of overflowRightEls) {
    if (el.width > cols) {
      el.width = cols
    }
    layout = moveElement({ layout, el, cols, compactType, x: 0, y: bottomBorder, isUserAction: false })
    bottomBorder = el.y + el.height
  }
  return compact(layout, compactType, cols)
}

const getPoz = (point: IPoint) => {
  const x = Math.round(point.x / 100)
  const y = Math.round(point.y / 100)
  return { x: Math.max(x, 0), y: Math.max(y, 0) }
}

const isElementsEqual = (l1: IGridItem, l2: IGridItem): boolean => {
  if (!l1 || !l2) return false
  if (l1.key !== l2.key) return false
  if (l1.height !== l2.height) return false
  if (l1.width !== l2.width) return false
  if (l1.x !== l2.x) return false
  if (l1.y !== l2.y) return false
  return true
}

const isLayoutsEqual = (l1: TLayout, l2: TLayout) => {
  if (l1.length !== l2.length) return false
  const l2Map = new Map(l2.map((item) => [item.key, item]))
  for (let item of l1) {
    if (!gridLayoutUtils.isElementsEqual(item, l2Map.get(item.key))) return false
  }
  return true
}

const gridLayoutUtils = {
  moveElement,
  compact,
  getPoz,
  compactItem,
  bottom,
  colCompact,
  isElementsEqual,
  isLayoutsEqual,
}

export default gridLayoutUtils
